import { newElementWith } from "@excalidraw/element";
import { pointFrom } from "@excalidraw/math";

import { isMindmapNode } from "./drawolaMindmapModel";

import type { LocalPoint } from "@excalidraw/math";
import {
  MINDMAP_DATA_KEY,
  MINDMAP_HORIZONTAL_GAP,
  MINDMAP_VERTICAL_GAP,
} from "./types";

import type { ExcalidrawElement } from "@excalidraw/element/types";

import type { MindmapNodeElement } from "./drawolaMindmapModel";

type LayoutTreeNode = {
  el: MindmapNodeElement;
  children: LayoutTreeNode[];
};

// Build the tree of nodes belonging to a single mindmap.
// Children inherit their order from the elements array (so the caller can
// control sibling order by splicing the new node at the right index before
// running layout).
const buildTree = (
  elements: readonly ExcalidrawElement[],
  rootEl: MindmapNodeElement,
): LayoutTreeNode => {
  const mindmapId = rootEl.customData[MINDMAP_DATA_KEY].mindmapId;
  const nodesInOrder: MindmapNodeElement[] = elements.filter(
    (el): el is MindmapNodeElement =>
      isMindmapNode(el) &&
      el.customData[MINDMAP_DATA_KEY].mindmapId === mindmapId,
  );

  const childrenByParent = new Map<string, MindmapNodeElement[]>();
  for (const node of nodesInOrder) {
    const parentId = node.customData[MINDMAP_DATA_KEY].parentId;
    if (parentId === null) {
      continue;
    }
    const list = childrenByParent.get(parentId) ?? [];
    list.push(node);
    childrenByParent.set(parentId, list);
  }

  const buildSubtree = (el: MindmapNodeElement): LayoutTreeNode => {
    const nodeId = el.customData[MINDMAP_DATA_KEY].nodeId;
    const childEls = childrenByParent.get(nodeId) ?? [];
    return {
      el,
      children: childEls.map(buildSubtree),
    };
  };

  return buildSubtree(rootEl);
};

// Total vertical extent of a subtree (max of own height and stacked
// children's combined height).
const getSubtreeHeight = (node: LayoutTreeNode): number => {
  if (node.children.length === 0) {
    return node.el.height;
  }
  const childrenHeight = node.children.reduce(
    (sum, child, i) =>
      sum + getSubtreeHeight(child) + (i > 0 ? MINDMAP_VERTICAL_GAP : 0),
    0,
  );
  return Math.max(node.el.height, childrenHeight);
};

// Place a non-root subtree, stacking its children outward (left or right of
// the parent depending on `direction`).
const layoutSubtree = (
  node: LayoutTreeNode,
  parentEdgeX: number,
  centerY: number,
  direction: 1 | -1,
  positions: Map<string, { x: number; y: number }>,
) => {
  const nodeX =
    direction === 1
      ? parentEdgeX + MINDMAP_HORIZONTAL_GAP
      : parentEdgeX - MINDMAP_HORIZONTAL_GAP - node.el.width;
  positions.set(node.el.id, {
    x: nodeX,
    y: centerY - node.el.height / 2,
  });

  if (node.children.length === 0) {
    return;
  }

  const totalChildHeight = node.children.reduce(
    (sum, child, i) =>
      sum + getSubtreeHeight(child) + (i > 0 ? MINDMAP_VERTICAL_GAP : 0),
    0,
  );
  const nextEdge = direction === 1 ? nodeX + node.el.width : nodeX;
  let cursorY = centerY - totalChildHeight / 2;
  for (const child of node.children) {
    const subH = getSubtreeHeight(child);
    layoutSubtree(child, nextEdge, cursorY + subH / 2, direction, positions);
    cursorY += subH + MINDMAP_VERTICAL_GAP;
  }
};

const computePositions = (
  rootEl: MindmapNodeElement,
  elements: readonly ExcalidrawElement[],
): Map<string, { x: number; y: number }> => {
  const tree = buildTree(elements, rootEl);
  const positions = new Map<string, { x: number; y: number }>();

  // Root stays anchored where the user placed it.
  positions.set(rootEl.id, { x: rootEl.x, y: rootEl.y });
  const rootCenterY = rootEl.y + rootEl.height / 2;

  const rightChildren = tree.children.filter(
    (c) => c.el.customData[MINDMAP_DATA_KEY].side === "R",
  );
  const leftChildren = tree.children.filter(
    (c) => c.el.customData[MINDMAP_DATA_KEY].side === "L",
  );

  const stack = (
    children: LayoutTreeNode[],
    edgeX: number,
    direction: 1 | -1,
  ) => {
    if (children.length === 0) {
      return;
    }
    const totalH = children.reduce(
      (sum, child, i) =>
        sum + getSubtreeHeight(child) + (i > 0 ? MINDMAP_VERTICAL_GAP : 0),
      0,
    );
    let cursorY = rootCenterY - totalH / 2;
    for (const child of children) {
      const subH = getSubtreeHeight(child);
      layoutSubtree(child, edgeX, cursorY + subH / 2, direction, positions);
      cursorY += subH + MINDMAP_VERTICAL_GAP;
    }
  };

  stack(rightChildren, rootEl.x + rootEl.width, 1);
  stack(leftChildren, rootEl.x, -1);

  return positions;
};

// Apply mindmap layout to the elements array: returns a new array with
// updated rect positions, bound text positions (translated by container
// delta), and arrow points (recomputed from binding fixed points).
export const applyMindmapLayout = (
  elements: readonly ExcalidrawElement[],
  rootEl: MindmapNodeElement,
): ExcalidrawElement[] => {
  const positions = computePositions(rootEl, elements);
  const elementById = new Map(elements.map((el) => [el.id, el]));
  const deltas = new Map<string, { dx: number; dy: number }>();

  for (const [id, pos] of positions) {
    const el = elementById.get(id);
    if (el) {
      const dx = pos.x - el.x;
      const dy = pos.y - el.y;
      if (dx !== 0 || dy !== 0) {
        deltas.set(id, { dx, dy });
      }
    }
  }

  return elements.map((el) => {
    const pos = positions.get(el.id);
    if (pos) {
      return newElementWith(el, { x: pos.x, y: pos.y });
    }

    if (el.type === "text" && el.containerId) {
      const delta = deltas.get(el.containerId);
      if (delta) {
        return newElementWith(el, {
          x: el.x + delta.dx,
          y: el.y + delta.dy,
        });
      }
    }

    if (
      el.type === "arrow" &&
      el.startBinding &&
      el.endBinding &&
      positions.has(el.startBinding.elementId) &&
      positions.has(el.endBinding.elementId)
    ) {
      const startEl = elementById.get(el.startBinding.elementId);
      const endEl = elementById.get(el.endBinding.elementId);
      const startPos = positions.get(el.startBinding.elementId);
      const endPos = positions.get(el.endBinding.elementId);
      if (startEl && endEl && startPos && endPos) {
        const sFP = el.startBinding.fixedPoint;
        const eFP = el.endBinding.fixedPoint;
        const sx = startPos.x + startEl.width * sFP[0];
        const sy = startPos.y + startEl.height * sFP[1];
        const ex = endPos.x + endEl.width * eFP[0];
        const ey = endPos.y + endEl.height * eFP[1];
        return newElementWith(el, {
          x: sx,
          y: sy,
          points: [
            pointFrom<LocalPoint>(0, 0),
            pointFrom<LocalPoint>(ex - sx, ey - sy),
          ],
        });
      }
    }

    return el;
  });
};
